"use client";

import Image from "@/components/ui/OptimizedImage";
import Link from "next/link";
import { buildEventPath } from "@/lib/routes";
import { LuClock } from "react-icons/lu";

import type { PublicEvent } from "./components/EventTypes";

interface EventRestrictedGateProps {
  event: PublicEvent;
  eventId: string;
  inviteId: string;
  isSignedIn: boolean | undefined;
  rsvpState: string;
  backgroundRef: React.RefObject<HTMLDivElement | null>;
  onGoBack: () => void;
  onRsvp: () => void;
  onSignIn: () => void;
}

export function EventRestrictedGate({
  event,
  eventId,
  inviteId,
  isSignedIn,
  rsvpState,
  backgroundRef,
  onGoBack,
  onRsvp,
  onSignIn,
}: EventRestrictedGateProps) {
  const gateBadge = event.accessControl === "INVITE_ONLY" ? "INVITE-ONLY" : "PRIVATE";
  const gateSubtext = isSignedIn
    ? event.accessControl === "INVITE_ONLY"
      ? "You don't have an invitation to this event. Request access below and the host will review your request."
      : "This event is private. Request access and the host will review your request."
    : event.accessControl === "INVITE_ONLY"
      ? "This event is invite-only. Sign in to check your invitation or request access from the host."
      : "Sign in to view this event and request access.";

  return (
    <main className="relative min-h-screen text-[var(--hopp-text)]">
      <div ref={backgroundRef} className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[#0e0e10]" />
      </div>
      <div className="relative z-10">
        <header className="fixed left-1/2 top-0 z-30 flex w-full max-w-7xl -translate-x-1/2 items-start justify-between px-4 pb-4 pt-10 lg:items-center lg:px-12 lg:pt-8">
          <button onClick={onGoBack} className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full text-[var(--hopp-text)] backdrop-blur-md transition bg-[rgba(28,28,32,0.6)]" aria-label="Go back">
            <span className="text-lg">&larr;</span>
          </button>
        </header>
        <div className="relative h-[360px] w-full lg:h-[460px]">
          {event.heroImage ? (
            <Image src={event.heroImage} alt={event.title} fill className="object-cover" sizes="100vw" priority />
          ) : (
            <div className="h-full w-full bg-[#0e0e10]" />
          )}
          <div className="absolute inset-0 backdrop-blur-[3px]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(14,14,16,0.98) 0%, rgba(14,14,16,0.7) 40%, rgba(14,14,16,0.4) 100%)" }} />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-6 pb-8 lg:px-12">
              <div className="mx-auto max-w-7xl space-y-3">
                <span className="inline-flex items-center rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-white/80 bg-[rgba(0,0,0,0.5)]">{gateBadge}</span>
                <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">{event.title}</h1>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="mx-auto mt-8 w-full max-w-md rounded-2xl p-6 text-center shadow-2xl bg-[rgba(28,28,32,0.72)] backdrop-blur-md border border-white/[0.06]">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.06]">
              <svg viewBox="0 0 24 24" className="h-6 w-6 text-[var(--hopp-muted)]" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 118 0v3" /></svg>
            </div>
            <h2 className="mt-4 text-base font-semibold text-white">This is a private event</h2>
            <p className="mt-2 text-sm text-[var(--hopp-muted)]">{gateSubtext}</p>
            {rsvpState === "pending" ? (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.06]">
                  <LuClock size={18} className="text-[var(--hopp-muted)]" />
                </div>
                <p className="text-sm text-[var(--hopp-muted)]">Your request has been sent. The host will review it and you&apos;ll be notified.</p>
              </div>
            ) : rsvpState === "rejected" ? (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.06]">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-[var(--hopp-muted)]" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                </div>
                <div className="text-center">
                  <p className="text-base font-semibold text-white">Your request was not approved</p>
                  <p className="mt-1 text-sm text-[var(--hopp-muted)]">You can request access again below.</p>
                </div>
                <button
                  onClick={() => {
                    if (!isSignedIn) {
                      onSignIn();
                    } else { onRsvp(); }
                  }}
                  className="mt-2 w-full rounded-full px-6 py-3 text-[10px] uppercase tracking-[0.3em] font-semibold text-[#f5f2ea] shadow-[0_10px_26px_rgba(58,92,255,0.3)] transition hover:opacity-90 active:scale-95 bg-[var(--hopp-gold)] disabled:opacity-60"
                >
                  {isSignedIn ? "Request access" : "Sign in"}
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  if (!isSignedIn) {
                    onSignIn();
                  } else { onRsvp(); }
                }}
                disabled={rsvpState === "loading"}
                className="mt-6 w-full rounded-full px-6 py-3 text-[10px] uppercase tracking-[0.3em] font-semibold text-[#f5f2ea] shadow-[0_10px_26px_rgba(58,92,255,0.3)] transition hover:opacity-90 active:scale-95 bg-[var(--hopp-gold)] disabled:opacity-60"
              >
                {rsvpState === "loading" ? "Saving..." : isSignedIn ? "Request access" : "Sign in"}
              </button>
            )}
            <p className="mt-3 text-[10px] text-[var(--hopp-muted)]">Have an invite link? Make sure you&apos;re signed in first.</p>
          </div>
        </div>
        <footer className="mx-auto mt-12 w-full max-w-7xl px-6 pb-20 text-center">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-[var(--hopp-muted)]">
            <Link href="/legal/terms" className="hover:text-white">Terms</Link>
            <Link href="/legal/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </footer>
      </div>
    </main>
  );
}
